import { Box, Dialog, makeStyles, useMediaQuery } from '@material-ui/core';
import createBreakpoints from '@material-ui/core/styles/createBreakpoints';
import { Spinner } from 'components';
import { ApplyForm } from 'components/jobseeker';
import { useNotifications } from 'context/NotificationsContext';
import { GoogleAnaliticsEvent, Theme } from 'enum';
import { Job, useApplyToJobMutation, useJobseekerProfileQuery } from 'graphql/generated';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logAnalyticsEvent } from 'ui-services/analytics.ui-service';
import { getFilteredRequirements } from 'ui-services/jobseeker.ui-service';

type EasyApplyDialogProps = {
    job: Job;
    setIsApplied?: any;
    open: boolean;
    setOpen?: any;
};

const useStyles = makeStyles((theme) => {
    return {
        dialog: {
            transition: '.5s',
            '& .MuiAlert-root': {
                padding: theme.spacing(0, 1, 0, 1),
                display: 'flex',
                alignItems: 'center',
            },
            '& .MuiButton-root': {
                transition: 'none',
            },
            '& a': {
                display: 'flex',
                width: '100%',
            },
        },
        spinner: {
            height: '327px',
            display: 'flex',
            alignItems: 'center',
        },
        closeIcon: {
            color: `${theme.palette.type === Theme.Light ? '#333' : '#fff'}`,
        },
    };
});

export const EasyApplyDialog: React.FC<EasyApplyDialogProps> = ({ job, setIsApplied, open, setOpen }) => {
    const { id: jobId } = job;
    const { t } = useTranslation();
    const { notify } = useNotifications();
    const [requirementAlert, setRequirementAlert] = useState(false);
    const breakpoints = createBreakpoints({});
    const isMobile = useMediaQuery(breakpoints.down('xs'));

    const { data: jobseekerProfile, loading } = useJobseekerProfileQuery({
        fetchPolicy: 'network-only',
    });

    const resumes = jobseekerProfile?.me?.jobseeker?.resumes.edges.map(({ node }) => node) || [];

    const requirements = job?.applicantProfileRequirements || [];
    const acceptIncompleteResumes = job?.acceptIncompleteResumes;

    useEffect(() => {
        if (requirements && jobseekerProfile) {
            const reqs = getFilteredRequirements(requirements, jobseekerProfile);
            if (acceptIncompleteResumes) {
                setRequirementAlert(false);
            } else {
                if (reqs && reqs?.length > 0) {
                    setRequirementAlert(true);
                } else {
                    setRequirementAlert(false);
                }
            }
        }
    }, [jobseekerProfile]);

    const closeDialog = (): void => setOpen(false);

    const [applyToJob] = useApplyToJobMutation({
        onCompleted() {
            logAnalyticsEvent(GoogleAnaliticsEvent.EASY_APPLIED);

            notify({
                type: 'success',
                message: t('jobDetails:applySuccessful'),
            });
            closeDialog();
            setIsApplied(true);
        },
    });

    const onSubmit = (formData): void => {
        applyToJob({
            variables: {
                jobId: jobId,
                coverLetter: formData.coverLetter,
            },
        });
    };

    const classes = useStyles();
    return (
        <Dialog classes={{ paper: classes.dialog }} open={open} fullScreen={isMobile} onClose={closeDialog}>
            {loading ? (
                <Box className={classes.spinner}>
                    <Spinner />
                </Box>
            ) : (
                <ApplyForm
                    closeDialog={closeDialog}
                    loading={loading}
                    onSubmit={onSubmit}
                    requirementAlert={requirementAlert}
                    requirements={requirements}
                    resumes={resumes}
                    title={t('jobDetails:applyToJob')}
                />
            )}
        </Dialog>
    );
};
